import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Eye, TrendingUp, Box, Package, ExternalLink, BarChart3 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_) {
    return iso;
  }
};

const SOURCE_LABELS = {
  product: 'Página do produto',
  catalog_3d: 'Catálogo 3D',
  home_showcase: 'Vitrine da Home',
};

const AdminMetrics3D = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_admin) {
      toast.error('Acesso negado.');
      navigate('/');
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/metrics/3d`);
        setData(res.data);
      } catch (err) {
        toast.error('Falha ao carregar métricas.');
      } finally {
        setLoading(false);
      }
    })();
  }, [authLoading, user, navigate]);

  const maxViews = useMemo(() => {
    if (!data?.products?.length) return 0;
    return data.products[0].views;
  }, [data]);

  if (authLoading || !user?.is_admin) return null;

  return (
    <div data-testid="admin-metrics-3d-page" className="py-20 md:py-32">
      <div className="px-6 md:px-12 lg:px-24 max-w-6xl mx-auto">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-primary mb-3"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Voltar ao painel
        </Link>
        <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight flex items-center gap-3">
          <BarChart3 className="w-8 h-8 text-primary" strokeWidth={1.5} />
          Métricas 3D
        </h1>
        <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
          Cada vez que um cliente abre o visualizador 3D de uma peça, contamos aqui.
          Rankeadas por número de visualizações — descubra quais peças mais chamam atenção.
        </p>

        {loading ? (
          <p className="text-muted-foreground py-16 text-center">Carregando métricas...</p>
        ) : !data?.products?.length ? (
          <div data-testid="metrics-empty" className="mt-12 text-center py-24 border border-dashed border-border">
            <Box className="w-14 h-14 mx-auto mb-4 text-neutral-300" strokeWidth={1} />
            <p className="text-muted-foreground mb-2">Ainda nenhuma visualização registrada.</p>
            <p className="text-xs text-muted-foreground">
              As métricas começam a contar assim que um cliente abrir o visualizador 3D pela primeira vez.
            </p>
          </div>
        ) : (
          <>
            {/* Total */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-10 mb-12">
              <div className="border border-border p-6 bg-neutral-50">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1">
                  <Eye className="w-3 h-3" strokeWidth={1.5} /> Total de visualizações
                </p>
                <p className="text-4xl font-light" data-testid="metrics-total-views">
                  {data.total_views.toLocaleString('pt-BR')}
                </p>
              </div>
              <div className="border border-border p-6 bg-neutral-50">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1">
                  <Package className="w-3 h-3" strokeWidth={1.5} /> Peças rastreadas
                </p>
                <p className="text-4xl font-light">{data.products.length}</p>
              </div>
              <div className="border border-border p-6 bg-primary text-white md:col-span-1 col-span-2">
                <p className="text-[10px] uppercase tracking-widest text-white/60 mb-2 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" strokeWidth={1.5} /> Peça mais popular
                </p>
                <p className="text-lg font-light truncate">{data.products[0].product_name}</p>
                <p className="text-xs text-white/70 mt-1">
                  {data.products[0].views} {data.products[0].views === 1 ? 'visualização' : 'visualizações'}
                </p>
              </div>
            </div>

            {/* Ranking */}
            <div className="space-y-2" data-testid="metrics-list">
              {data.products.map((item, idx) => {
                const barPct = maxViews > 0 ? (item.views / maxViews) * 100 : 0;
                return (
                  <div
                    key={item.product_id}
                    data-testid={`metrics-row-${item.product_id}`}
                    className="relative border border-border overflow-hidden bg-white"
                  >
                    {/* Barra visual proporcional */}
                    <div
                      className="absolute inset-y-0 left-0 bg-primary/5 pointer-events-none transition-all duration-500"
                      style={{ width: `${barPct}%` }}
                    />
                    <div className="relative flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4">
                      <div className="flex items-center gap-3 sm:contents">
                        <p className="text-xs uppercase tracking-widest text-muted-foreground w-8 shrink-0">
                          #{idx + 1}
                        </p>
                        <div className="w-14 h-14 bg-neutral-50 border border-border flex items-center justify-center shrink-0">
                          {item.product_image ? (
                            <img
                              src={item.product_image}
                              alt={item.product_name}
                              loading="lazy"
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <Box className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
                          )}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${item.product_deleted ? 'text-muted-foreground italic' : ''} break-words`}>
                          {item.product_name}
                          {item.product_deleted && ' (removido)'}
                        </p>
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                          Última: {formatDate(item.last_viewed_at)}
                        </p>
                        {Object.keys(item.views_by_source || {}).length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-1.5">
                            {Object.entries(item.views_by_source).map(([src, n]) => (
                              <span
                                key={src}
                                className="text-[9px] uppercase tracking-widest bg-neutral-100 border border-border px-2 py-0.5"
                              >
                                {SOURCE_LABELS[src] || src}: {n}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3 justify-between sm:justify-end sm:gap-4">
                        <div className="text-left sm:text-right shrink-0">
                          <p className="text-2xl font-light leading-none" data-testid={`metrics-views-${item.product_id}`}>
                            {item.views}
                          </p>
                          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                            {item.views === 1 ? 'visualização' : 'visualizações'}
                          </p>
                        </div>
                        {!item.product_deleted && (
                          <Link
                            to={`/produto/${item.product_id}`}
                            className="text-muted-foreground hover:text-primary p-2 shrink-0"
                            title="Abrir produto"
                          >
                            <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminMetrics3D;
